import isDate from 'lodash/isDate';
import isString from 'lodash/isString';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import moment from 'moment';
import isUndefined from 'lodash/isUndefined';
import pickBy from 'lodash/pickBy';
import pick from 'lodash/pick';

import {BaseTimeUnit, DateRangeType, RelativeDate, TimeUnit} from 'common/widgets/date-range-picker/models/types';

import {
  END_OF_TODAY,
  END_OF_YESTERDAY,
  START_OF_TODAY,
  START_OF_YESTERDAY,
  TIME_UNIT_QUERY_LIST,
  TIME_UNITS,
} from './constants';
import {ClauseType} from './types';

// Time constants
const SPECIAL_RANGES_SET = new Set([START_OF_YESTERDAY, END_OF_YESTERDAY, START_OF_TODAY, END_OF_TODAY]);
const TODAY = `$today`;

const TIME_UNIT_LIST = [TIME_UNITS.MINUTE, ...TIME_UNIT_QUERY_LIST];

export const RANGES = {
  TODAY: `Current day`,
  YESTERDAY: `Previous day`,
  HOURS: `Last 96 hours`,
  MONTH: `Last 30 days`,
  WEEKS: `Last 24 weeks`,
  YEAR: `Last 12 months`,
  CUSTOM: `Select a date range...`,
};

const RANGE_TO_VALUE_AND_UNIT = {
  [RANGES.TODAY]: {value: TODAY, unit: TIME_UNITS.HOUR},
  [RANGES.HOURS]: {value: 96, unit: TIME_UNITS.HOUR},
  [RANGES.MONTH]: {value: 30, unit: TIME_UNITS.DAY},
  [RANGES.WEEKS]: {value: 24, unit: TIME_UNITS.WEEK},
  [RANGES.YEAR]: {value: 12, unit: TIME_UNITS.MONTH},
};

const UNIT_AND_VALUE_TO_RANGE = {
  hour: {
    [TODAY]: RANGES.TODAY,
    96: RANGES.HOURS,
  },
  day: {
    30: RANGES.MONTH,
  },
  week: {
    24: RANGES.WEEKS,
  },
  month: {
    12: RANGES.YEAR,
  },
};

export const DATE_RANGE_TYPE = {
  BETWEEN: DateRangeType.Between,
  ON: DateRangeType.On,
  RELATIVE: DateRangeType.RelativeAfter,
  SINCE: DateRangeType.Since,
};

export const RANGE_TO_TIME_CLAUSE_PARAMS = {
  [RANGES.TODAY]: {
    dateRangeType: DATE_RANGE_TYPE.SINCE,
    value: START_OF_TODAY,
    window: null,
    unit: TIME_UNITS.HOUR,
  },
  [RANGES.YESTERDAY]: {
    dateRangeType: DATE_RANGE_TYPE.BETWEEN,
    value: [START_OF_YESTERDAY, END_OF_YESTERDAY],
    window: null,
    unit: TIME_UNITS.HOUR,
  },
  [RANGES.HOURS]: {
    dateRangeType: DATE_RANGE_TYPE.RELATIVE,
    value: null,
    window: {
      value: 96,
      unit: TIME_UNITS.HOUR,
    },
    unit: TIME_UNITS.HOUR,
  },
  [RANGES.MONTH]: {
    dateRangeType: DATE_RANGE_TYPE.RELATIVE,
    value: null,
    window: {
      value: 30,
      unit: TIME_UNITS.DAY,
    },
    unit: TIME_UNITS.DAY,
  },
  [RANGES.WEEKS]: {
    dateRangeType: DATE_RANGE_TYPE.RELATIVE,
    value: null,
    window: {
      value: 24,
      unit: TIME_UNITS.WEEK,
    },
    unit: TIME_UNITS.WEEK,
  },
  [RANGES.YEAR]: {
    dateRangeType: DATE_RANGE_TYPE.RELATIVE,
    value: null,
    window: {
      value: 12,
      unit: TIME_UNITS.MONTH,
    },
    unit: TIME_UNITS.MONTH,
  },
};
const NUMBER_OF_DAYS_IN_UNIT = {
  [TimeUnit.Minute]: 1 / 1440,
  [TimeUnit.Hour]: 1 / 24,
  [TimeUnit.Day]: 1,
  [TimeUnit.Week]: 7,
  [TimeUnit.Month]: 30,
  [TimeUnit.Quarter]: 90,
};

export function getNumberOfDaysInTimeClause(timeClause) {
  const [fromDate, toDate] = Array.isArray(timeClause.value) ? timeClause.value : [null, null];
  switch (timeClause.dateRangeType) {
    case DateRangeType.RelativeAfter:
      return Math.ceil(timeClause.window.value * NUMBER_OF_DAYS_IN_UNIT[timeClause.window.unit]);
    case DateRangeType.Since:
      return timeClause.value === START_OF_TODAY ? 1 : moment().diff(moment(fromDate), `days`);
    case DateRangeType.On:
      return 1;
    case DateRangeType.Between:
      // We could use moment#startOf for start of days but the ranges
      // need to be adjusted for project timezone
      if (fromDate === START_OF_TODAY && toDate === END_OF_TODAY) {
        return 1;
      }
      if (fromDate === START_OF_YESTERDAY && toDate === END_OF_YESTERDAY) {
        return 1;
      }
      return moment(toDate).diff(moment(fromDate), `days`);
    default:
      if (timeClause.value === TODAY) {
        return 1;
      } else if (isNumber(timeClause.value)) {
        return Math.ceil(timeClause.value * NUMBER_OF_DAYS_IN_UNIT[timeClause.unit]);
      } else {
        return moment(toDate).diff(moment(fromDate), `days`);
      }
  }
}

export class TimeClause {
  static TYPE: typeof ClauseType.Time = ClauseType.Time;

  static rangeToUnitValue(range): {unit: BaseTimeUnit; value: number | string} | EmptyObj {
    return RANGE_TO_VALUE_AND_UNIT[range] || {};
  }

  static unitValueToRange(unit, value): string | null {
    const unitVals = UNIT_AND_VALUE_TO_RANGE[unit];
    return (unitVals && unitVals[value]) || null;
  }

  dateRangeType: DateRangeType;
  unit: BaseTimeUnit;
  value: string[];
  rolling: boolean | null;
  window: RelativeDate;

  constructor(attrs: any = {}) {
    let unit;
    let value;
    if (attrs.range && !this.dateRangeType) {
      // Legacy format, to be supported for backwards compatibility
      ({unit, value} = TimeClause.rangeToUnitValue(attrs.range));
    } else {
      this.dateRangeType = attrs.dateRangeType;
      if (attrs.window) {
        this.window = attrs.window;
      }
      ({unit, value} = attrs);
    }
    if (unit) {
      this.unit = unit;
    }
    if (value) {
      this.value = value;
    }
    if (attrs.rolling) {
      this.rolling = attrs.rolling;
    }
  }

  extend(attrs = {}) {
    return new TimeClause({...this.attrs, ...attrs});
  }

  isValidNumberValue(value) {
    return isNumber(value) && value >= 0;
  }

  isValidValue(value) {
    return [
      this.isValidNumberValue(value),
      value === TODAY,
      isDate(value),
      Array.isArray(value) &&
        value.length <= 2 &&
        value.length > 0 &&
        (value.every(isString) || value.every(this.isValidNumberValue) || value.every(isDate)),
    ].some(Boolean);
  }

  isValidRelativeDate(date: RelativeDate) {
    return isObject(date) && TIME_UNIT_LIST.includes(date.unit as BaseTimeUnit) && this.isValidNumberValue(date.value);
  }

  isValidCalendarDateArray(value) {
    return (
      this.isValidDateStringArray(value) ||
      (this.rolling && Array.isArray(value) && value.every((v) => this.isValidRelativeDate(v)))
    );
  }

  isValidDateStringArray(value) {
    return (
      Array.isArray(value) &&
      value.every(
        (v) => isString(v) && (SPECIAL_RANGES_SET.has(v) || moment(v, `YYYY-MM-DD`, /* strict */ true).isValid()),
      )
    );
  }

  toBookmarkData() {
    return pickBy(this.attrs, (value) => !isUndefined(value));
  }

  toUrlData() {
    let keys = [];
    if (this.dateRangeType) {
      // new datepicker
      keys = [`dateRangeType`, `unit`].concat(
        this.dateRangeType === DATE_RANGE_TYPE.RELATIVE ? [`window`] : [`value`, `rolling`],
      );
    } else {
      // TODO: Can this codepath be removed?
      keys = [`value`, `unit`];
    }

    return pick(this, keys);
  }

  serialize() {
    return this.toUrlData();
  }

  get attrs() {
    return pick(this, `dateRangeType`, `window`, `unit`, `value`, `rolling`);
  }

  get valid() {
    const range = this.range;
    return [
      TIME_UNIT_LIST.includes(this.unit),
      this.dateRangeType !== DATE_RANGE_TYPE.RELATIVE || (this.window && this.isValidRelativeDate(this.window)),
      this.dateRangeType !== DATE_RANGE_TYPE.SINCE || range === RANGES.TODAY || this.isValidDateStringArray(this.value),
      this.dateRangeType !== DATE_RANGE_TYPE.ON || this.isValidCalendarDateArray(this.value),
      this.dateRangeType !== DATE_RANGE_TYPE.BETWEEN ||
        this.isValidCalendarDateArray(this.value) ||
        range === RANGES.YESTERDAY,
      this.dateRangeType || this.isValidValue(this.value),
    ].every(Boolean);
  }

  /**
   * Returns the number of days queried, for tracking purposes.
   */
  getNumberOfDaysQueried() {
    return getNumberOfDaysInTimeClause(this.attrs);
  }

  get range() {
    if (!this.dateRangeType) {
      return TimeClause.unitValueToRange(this.unit, this.value);
    } else {
      const entry = Object.entries(RANGE_TO_TIME_CLAUSE_PARAMS).find(([, {dateRangeType, window, unit, value}]) => {
        switch (dateRangeType) {
          case DATE_RANGE_TYPE.RELATIVE:
            return isEqual(this.window, window) && this.unit === unit;
          case DATE_RANGE_TYPE.SINCE:
            return !this.window && this.value === value;
          case DATE_RANGE_TYPE.BETWEEN:
            return isEqual(this.value, value);
          default:
            return false;
        }
      });
      return entry ? entry[0] : null;
    }
  }
}
